/**
 * @file Research dashboard API (healthcare practitioner/research endpoints).
 * @module api/research/researchApi
 *
 * Mirrors the XoeConversationsApi module pattern: ENV.VITE_HEALTHCARE_SERVER base through
 * the shared client (cookie session). Document download reuses the existing
 * globalFileResourcesApi — this module never builds a second download path.
 */

import { ENV } from '../../config/runtimeEnv';
import axios from 'axios';

import {
	KnowledgeSearchParams,
	KnowledgeSearchResponse,
	LiteratureArticleResponse,
	LiteratureSearchParams,
	LiteratureSearchResponse,
	ListResearchSearchesParams,
	ResearchSearch,
	SaveResearchSearchPayload,
} from '../../types/research/ResearchTypes';
import { http } from '../../lib/http/client';

const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

// ── Knowledge base ─────────────────────────────────────────────────────────────

/**
 * Search the clinic knowledge base (monographs, clinic documents, resource links).
 * Returns groups plus a non-blocking `unavailable` list of source types that could
 * not be searched.
 */
export const searchKnowledge = async (
	params: KnowledgeSearchParams,
): Promise<KnowledgeSearchResponse> => {
	const response = await http.get(
		`${HEALTHCARE_BASE_URL}/practitioner/research/knowledge`,
		{
			params: {
				q: params.q,
				...(params.sources ? { sources: params.sources } : {}),
				...(params.limit != null ? { limit: params.limit } : {}),
				// 1/0, not true/false: this is a query string, so a bare `false` serialises to the
				// string "false", which Laravel's `boolean` rule rejects (it accepts only
				// true/false/1/0/"1"/"0", strictly). The literature POST sends a real JSON boolean.
				...(params.record != null ? { record: params.record ? 1 : 0 } : {}),
			},
		},
	);
	return response.data;
};

// ── Medical literature (MedQuery) ──────────────────────────────────────────────

/**
 * Search medical literature via the MedQuery connector — a faithful pass-through of the
 * gateway's DSL (sort / offset paging / cursor deep paging / filters / facets).
 *
 * The body carries ONLY the keys the caller actually set: the gateway 400s on unknown keys,
 * so undefined values are omitted rather than sent as null. Facets must be requested in this
 * same call — never fan out one request per facet field (the per-key rate budget matters).
 *
 * When the connector is degraded the backend returns `{ available: false, message, … }` —
 * callers must surface that notice, never treat it as an error/blank state.
 */
export const searchLiterature = async (
	params: LiteratureSearchParams,
): Promise<LiteratureSearchResponse> => {
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/research/literature/search`,
		{
			q: params.q,
			...(params.sort ? { sort: params.sort } : {}),
			...(params.page != null ? { page: params.page } : {}),
			...(params.size != null ? { size: params.size } : {}),
			...(params.cursorMark ? { cursorMark: params.cursorMark } : {}),
			...(params.filters && params.filters.length > 0 ? { filters: params.filters } : {}),
			...(params.facets && params.facets.length > 0 ? { facets: params.facets } : {}),
			...(params.record != null ? { record: params.record } : {}),
		},
	);
	return response.data;
};

/**
 * Fetch one article's full PubMed record for the detail view.
 *
 * A 404 means the pmid is not in the current corpus (the corpus loads chronologically and is
 * partial) — that is an expected state, not a failure, so it is returned as the typed
 * `{ available: false, message }` envelope rather than thrown at the UI. Every other failure
 * still throws so React Query can surface a real error.
 */
export const getLiteratureArticle = async (
	pmid: string,
): Promise<LiteratureArticleResponse> => {
	try {
		const response = await http.get(
			`${HEALTHCARE_BASE_URL}/practitioner/research/literature/article/${encodeURIComponent(pmid)}`,
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) {
			const body = error.response.data as Partial<LiteratureArticleResponse> | undefined;
			return {
				available: false,
				message:
					(body && 'message' in body && typeof body.message === 'string' && body.message) ||
					'Article not found in the current corpus.',
			};
		}
		throw error;
	}
};

// ── Search history ──────────────────────────────────────────────────────────────

/** List the practitioner's recent research searches. */
export const listResearchSearches = async (
	params: ListResearchSearchesParams = {},
): Promise<ResearchSearch[]> => {
	const response = await http.get(
		`${HEALTHCARE_BASE_URL}/practitioner/research/searches`,
		{
			params: {
				...(params.limit != null ? { limit: params.limit } : {}),
				...(params.source ? { source: params.source } : {}),
			},
		},
	);
	// Backend wraps the list: { data: [...] }
	return response.data.data;
};

/** Persist a recent-search entry (origin defaults to 'dashboard' server-side). */
export const saveResearchSearch = async (
	payload: SaveResearchSearchPayload,
): Promise<ResearchSearch> => {
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/research/searches`,
		payload,
	);
	// Backend wraps: { data: search }
	return response.data.data;
};

/** Delete a single recent-search entry. */
export const deleteResearchSearch = async (id: number): Promise<void> => {
	await http.delete(
		`${HEALTHCARE_BASE_URL}/practitioner/research/searches/${id}`,
	);
};

/** Clear the practitioner's entire recent-search history. */
export const clearResearchSearches = async (): Promise<number> => {
	const response = await http.delete(
		`${HEALTHCARE_BASE_URL}/practitioner/research/searches`,
	);
	// Backend returns: { deleted: <n> }
	return response.data.deleted;
};
